/**
 * Pure detector + embed-URL deriver for Google file links surfaced in chat.
 *
 * The Google Docs/Slides/Sheets agents (and Drive) hand back editor URLs like
 * `https://docs.google.com/document/d/<id>/edit#heading=...`. To preview these
 * inline in the chat we rewrite them to Google's embeddable endpoints:
 *
 *   - Docs        /document/d/<id>     → /document/d/<id>/preview
 *   - Sheets      /spreadsheets/d/<id> → /spreadsheets/d/<id>/preview
 *   - Slides      /presentation/d/<id> → /presentation/d/<id>/embed
 *   - Drive file  /file/d/<id>         → /file/d/<id>/preview
 *
 * Scope (v1): only the canonical editor/file URL shapes above. Drive *folders*
 * (`/drive/folders/<id>`), `open?id=` short links, and any non-Google URL
 * return `null` — callers keep rendering those as plain links/buttons.
 *
 * Pure module — no imports. Safe for frontend bundles and unit tests.
 *
 * IMPORTANT (rendering reality, not a code limitation): the embed only renders
 * for a viewer whose browser is signed into a Google account with access to the
 * file. For everyone else Google 302s to its login wall, which refuses to be
 * framed — so the iframe shows a blank/refused page. A cross-origin iframe
 * cannot be probed from JS and the refused page is opaque, so this is handled
 * at the UI layer with an always-present "open in Google" fallback, not by
 * trying to detect failure here. See `src/components/chat/GoogleFileEmbed.tsx`.
 */

export type GoogleFileKind = "doc" | "sheets" | "slides" | "drive";

export interface GoogleEmbed {
  readonly kind: GoogleFileKind;
  readonly fileId: string;
  readonly embedUrl: string;
  readonly sourceUrl: string;
}

interface Matcher {
  readonly kind: GoogleFileKind;
  readonly re: RegExp;
  readonly toEmbed: (fileId: string) => string;
}

// File ids are URL-safe (`[\w-]+`). Each pattern anchors on the host + path
// segment so a stray id-shaped string elsewhere can't match.
const MATCHERS: readonly Matcher[] = [
  {
    kind: "doc",
    re: /^https:\/\/docs\.google\.com\/document\/d\/([\w-]+)/,
    toEmbed: (id) => `https://docs.google.com/document/d/${id}/preview`,
  },
  {
    kind: "sheets",
    re: /^https:\/\/docs\.google\.com\/spreadsheets\/d\/([\w-]+)/,
    toEmbed: (id) => `https://docs.google.com/spreadsheets/d/${id}/preview`,
  },
  {
    kind: "slides",
    re: /^https:\/\/docs\.google\.com\/presentation\/d\/([\w-]+)/,
    toEmbed: (id) => `https://docs.google.com/presentation/d/${id}/embed`,
  },
  {
    kind: "drive",
    re: /^https:\/\/drive\.google\.com\/file\/d\/([\w-]+)/,
    toEmbed: (id) => `https://drive.google.com/file/d/${id}/preview`,
  },
];

/**
 * Returns the embed descriptor for a supported Google file URL, or `null` if
 * the URL isn't an embeddable Google file. The query string and `#fragment`
 * (e.g. `/edit#heading=h.abc`) are ignored — only the host + `/d/<id>` segment
 * drive the match.
 */
export function detectGoogleEmbed(url: string): GoogleEmbed | null {
  if (typeof url !== "string" || url.length === 0) return null;
  for (const { kind, re, toEmbed } of MATCHERS) {
    const match = re.exec(url);
    if (match) {
      const fileId = match[1];
      return { kind, fileId, embedUrl: toEmbed(fileId), sourceUrl: url };
    }
  }
  return null;
}
